import React, { useEffect, useState, useCallback } from 'react';
import styles from './ComparisonResult.module.scss';
import {
  ComparisonReportResponse,
  IComparisonResult
} from './ComparisonResult.interface';
import { Button } from '@zurich-primitives/button';
import { ReactComponent as DiagramIcon } from 'app/assets/Icons/Chips/Diagram.svg';
import { ReactComponent as PackageIcon } from 'app/assets/Icons/Chips/Package.svg';
import { ReactComponent as TargetIcon } from 'app/assets/Icons/Chips/Target.svg';

export const ComparisonResult: React.FC<IComparisonResult> = ({
  comparisonId,
  isProcessing = false,
  initialReport
}) => {
  const [activeTab, setActiveTab] = useState<
    'combined' | 'outline' | 'sideBySide'
  >('combined');
  const [report] = useState<ComparisonReportResponse | null>(
    initialReport ?? null
  );
  const [polling, setPolling] = useState<boolean>(isProcessing);

  const fetchReport = useCallback(
    async (silent = false) => {
      // No-op: loading state removed as it's unused
      try {
        console.log('⚠️ API fetch disabled - using local comparison mode');

        setPolling(false);
      } catch (e) {
        // Log for debugging; API fetch is disabled in this mode
        console.debug('fetchReport error (ignored in local mode):', e);
      } finally {
        // No-op: loading state removed as it's unused
      }
    },
    [comparisonId]
  );

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(() => {
      // Discard the returned Promise to satisfy lint rule without changing behavior
      void fetchReport(true);
    }, 4000);
    return () => clearInterval(interval);
  }, [polling, fetchReport]);

  const artifactPending = (url?: string) => !url || /\/null(\?|$)/.test(url);

  const renderTabContent = () => {
    if (!report) return null;

    const tabMap: Record<string, string | undefined> = {
      combined: report.resultFile1,
      outline: report.resultFile3,
      sideBySide: report.resultFile2
    };
    const currentUrl = tabMap[activeTab];
    const pending = artifactPending(currentUrl);
    return (
      <>
        {pending && (
          <div className={styles.spinnerOverlay}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  border: '6px solid #d3e3f5',
                  borderTopColor: '#0b62c1',
                  borderRadius: '50%',
                  animation: 'spin 0.9s linear infinite'
                }}
              />
              <div style={{ fontSize: 13, color: '#0b3b68', fontWeight: 500 }}>
                Generating {activeTab.replaceAll(/([A-Z])/g, ' $1')}…
              </div>
              <div style={{ fontSize: 11, color: '#47607a' }}>
                Comparison ID: {comparisonId}
              </div>
            </div>
          </div>
        )}
        {!pending && currentUrl && (
          <iframe
            title={activeTab}
            src={currentUrl}
            className={styles.iframe}
          />
        )}
      </>
    );
  };

  return (
    <div className={styles['comparison-result']}>
      <h2 className={styles['report-name']}>
        {report?.name ?? 'Document Comparison'}
      </h2>
      <div className={styles.tabs}>
        <Button
          variant={activeTab === 'combined' ? 'secondary' : 'tertiary'}
          size="small"
          onClick={() => setActiveTab('combined')}
          aria-label="Combined View"
        >
          <Button.Icon>
            <PackageIcon />
          </Button.Icon>
          <Button.Label>Combined View</Button.Label>
        </Button>
        <Button
          variant={activeTab === 'outline' ? 'secondary' : 'tertiary'}
          size="small"
          onClick={() => setActiveTab('outline')}
          aria-label="Combined with Outline"
        >
          <Button.Icon>
            <DiagramIcon />
          </Button.Icon>
          <Button.Label>Combined + Outline</Button.Label>
        </Button>
        <Button
          variant={activeTab === 'sideBySide' ? 'secondary' : 'tertiary'}
          size="small"
          onClick={() => setActiveTab('sideBySide')}
          aria-label="Side by Side"
        >
          <Button.Icon>
            <TargetIcon />
          </Button.Icon>
          <Button.Label>Side by Side</Button.Label>
        </Button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Button
            variant="tertiary"
            size="small"
            onClick={() => {
              void fetchReport();
            }}
            aria-label="Refresh"
          >
            <Button.Label>Refresh</Button.Label>
          </Button>
        </div>
      </div>
      <div className={styles['tab-content']}>{renderTabContent()}</div>
      {report && (
        <div className={styles.linksRow}>
          {report.resultFile1 && (
            <a
              href={report.resultFile1}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Combined ↗
            </a>
          )}
          {report.resultFile3 && (
            <a
              href={report.resultFile3}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Outline ↗
            </a>
          )}
          {report.resultFile2 && (
            <a
              href={report.resultFile2}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Side-by-Side ↗
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default ComparisonResult;
